import { FacilityCostSource, isPublicFacility } from "./facilityCostRange";

/**
 * 장지 견적 결과 화면의 가치제안 셀.
 *
 * 공설은 지자체 직계약이라 장지 계약 수수료가 없다. 그래서 두 셀이 팔아야 하는 것이 다르다.
 *
 * - `public`: 장지 비용은 이미 정리됐다. 남은 큰 돈은 장례식장이라고 알려 상조로 넘긴다
 * - `private`: 자리를 미리 봐야 한다. 장례식장에서 아낀 돈이 장지 부담을 줄인다고 알려 상조로 넘긴다
 *
 * 두 셀 다 목적지가 상조인 것이 의도다. 실험 #324(O/X 퀴즈)가 이 화면에서 장지 콘텐츠만
 * 늘렸다가 goi_call 41.0% -> 12.8% 로 졌고, 종료 사유가 "장지 상담은 늘었으나 장례 상담이
 * 줄어 유효리드 감소" 였다. 같은 실패를 구조적으로 피한다.
 *
 * 수도권/지방 축은 넣지 않는다. 개별 장지 견적 8,787건 실측에서 "공설 x 수도권" 이 1,240건인데
 * 그중 서울이 4건(0.3%)이고 나머지 1,236건이 경기, 인천이다. 그 두 지역은 공설이 사설보다
 * 많고 저렴해서(인천 공설 봉안당 10~25만원 vs 사설 380~1,080만원) "수도권 공설은 만장이니
 * 사설을 보라" 가 99.7%에게 틀린 말이 된다. 지역은 별도 단계에서 다룬다.
 */
export type JangjiResultCell = "public" | "private";

/**
 * 공설 시설명 목록. **`getStaticProps` 에서만 호출할 것.**
 *
 * `facilities_detail.json` 전체(2.1MB)를 훑으므로 컨테이너에서 호출하면 클라이언트 번들에
 * 실린다. 결과 페이지에는 이름 목록(591개, 약 20KB)만 내려보낸다.
 *
 * ── 왜 sessionStorage 의 `cell` 을 쓰지 않는가
 *
 * 로그인 화면 실험(#336)이 `jangji_quote_context.cell` 을 담아 두지만, 그 마킹은 제출부의
 * 비로그인 분기에서만 생긴다. 두 가지 구멍이 있다.
 *
 * 1. 이미 로그인한 상태로 제출한 사람은 마킹이 없다
 * 2. 결과 페이지 재방문에 마킹이 없다. GA4 기준 이 페이지는 유저당 4회 열린다
 *    (28일 pageview 13,238 / users 3,261)
 *
 * 시설명은 `memo` 에 남아 서버에서 다시 내려오므로, 이름으로 판정하면 두 구멍이 모두 막힌다.
 *
 * @param all facilities_detail.json 전체
 * @returns 공설로 판정된 시설명 배열
 */
export function buildPublicFacilityNames(
  all: readonly (FacilityCostSource & { companyname?: string })[],
): string[] {
  const names = new Set<string>();

  for (const facility of all) {
    const name = facility.companyname?.trim();
    if (!name) continue;
    if (isPublicFacility(facility)) names.add(name);
  }

  return Array.from(names);
}

/**
 * 시설명으로 셀을 정한다.
 *
 * 목록에 없으면 `private` 이다. `isPublicFacility` 가 `is_public` 플래그와 이름 신호를 OR 로
 * 묶어 이미 넓게 잡으므로(완도군추모공원봉안당처럼 플래그가 false 인 실질 공설을 포함),
 * 여기서 못 걸린 것은 사설로 보는 편이 맞다.
 *
 * @param facilityName 개별 장지 견적의 memo 에서 뽑은 시설명. 없으면 null (실험 모수 밖)
 * @param publicNames buildPublicFacilityNames 결과로 만든 Set
 * @returns 셀. 시설명이 없으면 null
 */
export function resolveJangjiResultCell(
  facilityName: string | undefined,
  publicNames: ReadonlySet<string>,
): JangjiResultCell | null {
  if (!facilityName) return null;
  return publicNames.has(facilityName.trim()) ? "public" : "private";
}
